import { useState } from 'react';
import { Card, CardContent, Button } from '@/components/ui';
import { metricGroupApi } from '@/services/metric-group-api';
import type {
  ICreateHomepageMetricGroupRequest,
  IHomepageMetricGroup,
} from '@skystone/shared';
import { MetricGroupFormFields } from './MetricGroupFormFields';
import {
  emptyMetricGroupFormState,
  validateMetricGroupLinkTarget,
  validateMetricGroupTitle,
  type MetricGroupFormState,
} from './metric-group-form-helpers';

const GROUP_KEY_REGEX = /^[a-z][a-z0-9_]{2,49}$/;

function validateGroupKey(value: string): string | null {
  if (value === '') return 'Group key is required';
  if (!GROUP_KEY_REGEX.test(value)) {
    return '3-50 chars, lowercase letters, digits, or underscores; must start with a letter';
  }
  return null;
}

interface CreateMetricGroupCardProps {
  onCreated: (group: IHomepageMetricGroup) => void;
}

export function CreateMetricGroupCard({
  onCreated,
}: Readonly<CreateMetricGroupCardProps>) {
  const [expanded, setExpanded] = useState(false);
  const [groupKey, setGroupKey] = useState('');
  const [form, setForm] = useState<MetricGroupFormState>(emptyMetricGroupFormState);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const groupKeyError = validateGroupKey(groupKey);
  const titleError = validateMetricGroupTitle(form.title);
  const linkTargetError = validateMetricGroupLinkTarget(form.linkTarget);
  const isValid =
    groupKeyError === null && titleError === null && linkTargetError === null;

  function setField<K extends keyof MetricGroupFormState>(key: K, value: MetricGroupFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetForm() {
    setGroupKey('');
    setForm(emptyMetricGroupFormState);
    setServerError(null);
  }

  async function handleCreate() {
    setSaving(true);
    setServerError(null);
    try {
      const body: ICreateHomepageMetricGroupRequest = { groupKey, ...form };
      const created = await metricGroupApi.create(body);
      onCreated(created);
      resetForm();
      setExpanded(false);
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  if (!expanded) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-3 p-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">Add a new metric group</h2>
            <p className="text-xs text-muted-foreground">
              Creates an additional card on the homepage. Set it visible to show it to users.
            </p>
          </div>
          <Button size="sm" onClick={() => setExpanded(true)}>
            Add new group
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <header className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-foreground">New metric group</h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              resetForm();
              setExpanded(false);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        </header>

        {serverError && (
          <p className="text-xs text-error" role="alert">{serverError}</p>
        )}

        <div>
          <label
            className="mb-1 block text-xs font-medium text-muted-foreground"
            htmlFor="new-metric-group-key"
          >
            Group key (immutable after create)
          </label>
          <input
            id="new-metric-group-key"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none md:w-1/2"
            value={groupKey}
            onChange={(e) => setGroupKey(e.target.value)}
            maxLength={50}
            placeholder="devops_platform"
            aria-invalid={groupKeyError !== null && groupKey !== ''}
          />
          {groupKey !== '' && groupKeyError && (
            <p className="mt-1 text-xs text-error" role="alert">{groupKeyError}</p>
          )}
        </div>

        <MetricGroupFormFields
          form={form}
          setField={setField}
          titleError={titleError}
          linkTargetError={linkTargetError}
          trailing={
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={saving || !isValid}
            >
              {saving ? 'Creating...' : 'Create'}
            </Button>
          }
        />
      </CardContent>
    </Card>
  );
}
